import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../common/api/http";
import { EntityIdObject, EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../common/types";
import { UUID } from "../../typings/global";
import { AgentCompletedEducation, AgentEducationsRequest } from "../agent/types";
import {
  AgentExpertisesDashboard,
  AgentMlmWithSubordinates,
  ContractStatisticsDashboard,
  ContractStatusesDashboard,
  CreateDashboardGoal,
  CreateUpdateDashboardContact,
  CreateUpdateDashboardNotice,
  DashboardCommissionsRequest,
  DashboardCommissionsResponse,
  DashboardContact,
  DashboardContactSearchRequest,
  DashboardContactSearchResult,
  DashboardEventResponse,
  DashboardGoal,
  DashboardGoalRequest,
  DashboardMinMaxDatesRequest,
  DashboardNotice,
  DashboardNoticeAttachment,
  DashboardNoticeFilterPageRequest,
  DashboardNoticeFilterPageResult,
  DashboardPortfolioResponse,
  DashboardStatistics,
  DashboardWidgetSettings,
  DefaultDashboardWidgetSettings,
  ExtendedDashboardStatistics,
  PersonalDatesDashboard,
  UpdateDashboardGoal,
  UpdateDashboardWidgetSettings,
} from "./types";

const GET_DASHBOARD_STATISTICS = ApiRequestAdapter.create("/dashboard/statistics", HttpMethod.GET);
const GET_DASHBOARD_EXTENDED_STATISTICS = ApiRequestAdapter.create("/dashboard/extended-statistics", HttpMethod.GET);
const GET_DASHBOARD_PERSONAL_DATES = ApiRequestAdapter.create("/dashboard/personal-dates", HttpMethod.GET);
const GET_DASHBOARD_AGENT_EXPERTISES = ApiRequestAdapter.create("/dashboard/agent-expertises", HttpMethod.GET);
const GET_DASHBOARD_AGENT_EDUCATIONS = ApiRequestAdapter.create("/dashboard/agent-educations", HttpMethod.GET);
const GET_DASHBOARD_CONTRACT_STATUSES = ApiRequestAdapter.create("/dashboard/contract-statuses", HttpMethod.GET);
const GET_DASHBOARD_CONTRACT_STATISTICS = ApiRequestAdapter.create("/dashboard/contract-statistics", HttpMethod.GET);
const GET_DASHBOARD_MLM_STRUCTURE = ApiRequestAdapter.create("/dashboard/mlm-structure", HttpMethod.GET);
const GET_DASHBOARD_EVENTS = ApiRequestAdapter.create("/dashboard/events", HttpMethod.GET);
const GET_DASHBOARD_COMMISSIONS = ApiRequestAdapter.create("/dashboard/commissions", HttpMethod.GET);
const GET_DASHBOARD_PORTFOLIO = ApiRequestAdapter.create("/dashboard/portfolio", HttpMethod.GET);

const FILTER_DASHBOARD_NOTICES = ApiRequestAdapter.create("/dashboard/notices", HttpMethod.GET);
const CREATE_DASHBOARD_NOTICE = ApiRequestAdapter.create("/dashboard/notices", HttpMethod.POST);
const UPDATE_DASHBOARD_NOTICE = ApiRequestAdapter.create("/dashboard/notices/{0}", HttpMethod.PUT);
const DELETE_DASHBOARD_NOTICE = ApiRequestAdapter.create("/dashboard/notices/{0}", HttpMethod.DELETE);
const MARK_AS_SEEN_DASHBOARD_NOTICES = ApiRequestAdapter.create("/dashboard/notices/mark-as-seen", HttpMethod.PUT);
const MARK_ALL_AS_SEEN_DASHBOARD_NOTICES = ApiRequestAdapter.create(
  "/dashboard/notices/mark-all-as-seen",
  HttpMethod.PUT,
);
const TOGGLE_SEEN_DASHBOARD_NOTICE = ApiRequestAdapter.create("/dashboard/notices/{0}/toggle-seen", HttpMethod.PUT);
const ADD_DASHBOARD_NOTICE_ATACHMENTS = ApiRequestAdapter.create("/dashboard/notices/{0}/attachments", HttpMethod.POST);
const DOWNLOAD_DASHBOARD_NOTICE_ATACHMENT = ApiRequestAdapter.create(
  "/dashboard/notices/{0}/attachments/{1}",
  HttpMethod.GET,
);
const DELETE_DASHBOARD_NOTICE_ATACHMENT = ApiRequestAdapter.create(
  "/dashboard/notices/{0}/attachments/{1}",
  HttpMethod.DELETE,
);

const SEARCH_DASHBOARD_CONTACTS = ApiRequestAdapter.create("/dashboard/contacts", HttpMethod.GET);
const CREATE_DASHBOARD_CONTACT = ApiRequestAdapter.create("/dashboard/contacts", HttpMethod.POST);
const UPDATE_DASHBOARD_CONTACT = ApiRequestAdapter.create("/dashboard/contacts/{0}", HttpMethod.PUT);
const DELETE_DASHBOARD_CONTACT = ApiRequestAdapter.create("/dashboard/contacts/{0}", HttpMethod.DELETE);

const GET_DASHBOARD_GOALS = ApiRequestAdapter.create("/dashboard/{0}/goals", HttpMethod.GET);
const CREATE_DASHBOARD_GOALS = ApiRequestAdapter.create("/dashboard/{0}/goals", HttpMethod.POST);
const UPDATE_DASHBOARD_GOALS = ApiRequestAdapter.create("/dashboard/{0}/goals/{1}", HttpMethod.PUT);
const DELETE_DASHBOARD_GOALS = ApiRequestAdapter.create("/dashboard/{0}/goals/{1}", HttpMethod.DELETE);

const GET_DASHBOARD_WIDGET_SETTINGS = ApiRequestAdapter.create("/dashboard/widget-settings", HttpMethod.GET);
const UPDATE_DASHBOARD_WIDGET_SETTINGS = ApiRequestAdapter.create("/dashboard/widget-settings", HttpMethod.PUT);
const GET_DEFAULT_DASHBOARD_WIDGET_SETTINGS = ApiRequestAdapter.create(
  "/dashboard/widget-settings/default",
  HttpMethod.GET,
);

export const dashboardApi = {
  getDashboardStatistics: (): AxiosPromise<DashboardStatistics> => {
    return apiService.get(GET_DASHBOARD_STATISTICS.url);
  },
  getDashboardExtendedStatistics: (): AxiosPromise<ExtendedDashboardStatistics> => {
    return apiService.get(GET_DASHBOARD_EXTENDED_STATISTICS.url);
  },
  getDashboardPersonalDates: (request: DashboardMinMaxDatesRequest): AxiosPromise<PersonalDatesDashboard> => {
    return apiService.get(GET_DASHBOARD_PERSONAL_DATES.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getDashboardAgentExpertises: (): AxiosPromise<AgentExpertisesDashboard> => {
    return apiService.get(GET_DASHBOARD_AGENT_EXPERTISES.url);
  },
  getDashboardAgentEducations: (request: AgentEducationsRequest): AxiosPromise<AgentCompletedEducation[]> => {
    return apiService.get(GET_DASHBOARD_AGENT_EDUCATIONS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getDashboardContractStatuses: (): AxiosPromise<ContractStatusesDashboard> => {
    return apiService.get(GET_DASHBOARD_CONTRACT_STATUSES.url);
  },
  getDashboardContractStatistics: (request: DashboardMinMaxDatesRequest): AxiosPromise<ContractStatisticsDashboard> => {
    return apiService.get(GET_DASHBOARD_CONTRACT_STATISTICS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getDashboardMlmStructure: (): AxiosPromise<AgentMlmWithSubordinates> => {
    return apiService.get(GET_DASHBOARD_MLM_STRUCTURE.url);
  },
  getDashboardEvents: (request: DashboardMinMaxDatesRequest): AxiosPromise<DashboardEventResponse> => {
    return apiService.get(GET_DASHBOARD_EVENTS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getDashboardCommissions: (request: DashboardCommissionsRequest): AxiosPromise<DashboardCommissionsResponse> => {
    return apiService.get(GET_DASHBOARD_COMMISSIONS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getDashboardPortfolio: (request: DashboardMinMaxDatesRequest): AxiosPromise<DashboardPortfolioResponse> => {
    return apiService.get(GET_DASHBOARD_PORTFOLIO.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  filterDashboardNotices: (
    request: DashboardNoticeFilterPageRequest,
  ): AxiosPromise<DashboardNoticeFilterPageResult> => {
    return apiService.get(FILTER_DASHBOARD_NOTICES.url, { params: request, paramsSerializer: serializeParams });
  },
  createDashboardNotice: (request: CreateUpdateDashboardNotice): AxiosPromise<DashboardNotice> => {
    return apiService.post(CREATE_DASHBOARD_NOTICE.url, request);
  },
  updateDashboardNotice: (request: EntityObject<CreateUpdateDashboardNotice>): AxiosPromise<DashboardNotice> => {
    return apiService.put(UPDATE_DASHBOARD_NOTICE.getParametrizedUrl(request.id), request.object);
  },
  deleteDashboardNotice: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DASHBOARD_NOTICE.getParametrizedUrl(request.id));
  },
  markAsSeenDashboardNotices: (request: { noticeIds: UUID[] }): AxiosPromise<DashboardNotice[]> => {
    return apiService.put(MARK_AS_SEEN_DASHBOARD_NOTICES.url, request);
  },
  markAllAsSeenDashboardNotices: (): AxiosPromise => {
    return apiService.put(MARK_ALL_AS_SEEN_DASHBOARD_NOTICES.url);
  },
  toggleSeenDashboardNotice: (request: EntityIdObject): AxiosPromise<DashboardNotice> => {
    return apiService.put(TOGGLE_SEEN_DASHBOARD_NOTICE.getParametrizedUrl(request.id));
  },
  addDashboardNoticeAttachments: (request: EntityObject<FormData>): AxiosPromise<DashboardNoticeAttachment[]> => {
    return apiService.post(ADD_DASHBOARD_NOTICE_ATACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadDashboardNoticeAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_DASHBOARD_NOTICE_ATACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  deleteDashboardNoticeAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DASHBOARD_NOTICE_ATACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
  searchDashboardContacts: (request: DashboardContactSearchRequest): AxiosPromise<DashboardContactSearchResult> => {
    return apiService.get(SEARCH_DASHBOARD_CONTACTS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createDashboardContact: (request: CreateUpdateDashboardContact): AxiosPromise<DashboardContact> => {
    return apiService.post(CREATE_DASHBOARD_CONTACT.url, request);
  },
  updateDashboardContact: (request: EntityObject<CreateUpdateDashboardContact>): AxiosPromise<DashboardContact> => {
    return apiService.put(UPDATE_DASHBOARD_CONTACT.getParametrizedUrl(request.id), request.object);
  },
  deleteDashboardContact: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DASHBOARD_CONTACT.getParametrizedUrl(request.id));
  },
  getDashboardGoals: (request: EntityObject<DashboardGoalRequest>): AxiosPromise<DashboardGoal[]> => {
    return apiService.get(GET_DASHBOARD_GOALS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createDashboardGoal: (request: EntityObject<CreateDashboardGoal>): AxiosPromise<DashboardGoal> => {
    return apiService.post(CREATE_DASHBOARD_GOALS.getParametrizedUrl(request.id), request.object);
  },
  updateDashboardGoal: (request: TwoLevelEntityObject<UpdateDashboardGoal>): AxiosPromise<DashboardGoal> => {
    return apiService.put(UPDATE_DASHBOARD_GOALS.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteDashboardGoal: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DASHBOARD_GOALS.getParametrizedUrl(request.id1, request.id2));
  },
  getDashboardWidgetSettings: (): AxiosPromise<DashboardWidgetSettings[]> => {
    return apiService.get(GET_DASHBOARD_WIDGET_SETTINGS.url);
  },
  updateDashboardWidgetSettings: (
    request: UpdateDashboardWidgetSettings[],
  ): AxiosPromise<DashboardWidgetSettings[]> => {
    return apiService.put(UPDATE_DASHBOARD_WIDGET_SETTINGS.url, request);
  },
  getDefaultDashboardWidgetSettings: (): AxiosPromise<DefaultDashboardWidgetSettings[]> => {
    return apiService.get(GET_DEFAULT_DASHBOARD_WIDGET_SETTINGS.url);
  },
};
